import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminShell } from './AdminShell';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { ApiError, auditApi } from '../../lib/api';
import type { AuditEvent, AuditKind, AuditPage, AuditQuery } from '../../lib/types';

const PER_PAGE = 50;

const KIND_LABELS: Record<AuditKind, string> = {
  login: 'Sign-in',
  login_failed: 'Failed sign-in',
  logout: 'Sign-out',
  request: 'API call',
  server_error: 'Server error',
  client_error: 'Browser error',
};

const LEVEL_LABELS = { info: 'Info', warn: 'Warning', error: 'Error' } as const;

/** Typed filters wait for a pause before asking the API again. */
function useDebounced<T>(value: T, ms = 350): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

function when(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
}

/**
 * /web/Admin/AuditLogs — who signed in, which part of the API they used, and
 * what failed, on the server or in a browser. Superadmin only; the API refuses
 * anyone else.
 */
export function AuditLogs() {
  // Users → Audit log opens this page on one person: ?email=…
  const [params] = useSearchParams();
  const [kind, setKind] = useState<AuditQuery['kind']>('');
  const [level, setLevel] = useState<AuditQuery['level']>('');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [text, setText] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [reloads, setReloads] = useState(0);

  const [data, setData] = useState<AuditPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  const typedEmail = useDebounced(email);
  const typedText = useDebounced(text);

  // A new filter starts again from the first page.
  const filter = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    // The day picked as "to" is included: the API's bound is exclusive.
    const until = to ? new Date(new Date(`${to}T00:00:00Z`).getTime() + 86_400_000).toISOString().slice(0, 10) : '';

    auditApi
      .list({ kind, level, email: typedEmail, q: typedText, from, to: until, page, perPage: PER_PAGE })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load the audit log.');
      });
    return () => {
      cancelled = true;
    };
  }, [kind, level, typedEmail, typedText, from, to, page, reloads]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.perPage)) : 1;

  return (
    <AdminShell
      active="AuditLogs"
      title="Audit Logs"
      count={data ? `${data.total.toLocaleString()} events` : undefined}
      action={
        <button type="button" className="cmbtn" onClick={() => setReloads((n) => n + 1)}>
          Refresh
        </button>
      }
    >
      <div className="cmtools audittools">
        <select
          className="cmselect"
          aria-label="Kind"
          value={kind}
          onChange={(e) => filter(setKind)(e.target.value as AuditQuery['kind'])}
        >
          <option value="">Kind: All</option>
          {(data?.kinds ?? (Object.keys(KIND_LABELS) as AuditKind[])).map((k) => (
            <option key={k} value={k}>
              {KIND_LABELS[k] ?? k}
            </option>
          ))}
        </select>
        <select
          className="cmselect"
          aria-label="Level"
          value={level}
          onChange={(e) => filter(setLevel)(e.target.value as AuditQuery['level'])}
        >
          <option value="">Level: All</option>
          <option value="info">Info</option>
          <option value="warn">Warning</option>
          <option value="error">Error</option>
        </select>
        <input
          type="search"
          className="cmsearch"
          placeholder="User email…"
          aria-label="User email"
          value={email}
          onChange={(e) => filter(setEmail)(e.target.value)}
        />
        <input
          type="search"
          className="cmsearch"
          placeholder="Path, message or request id…"
          aria-label="Path, message or request id"
          value={text}
          onChange={(e) => filter(setText)(e.target.value)}
        />
        <label className="auditdate">
          From
          <input type="date" value={from} onChange={(e) => filter(setFrom)(e.target.value)} />
        </label>
        <label className="auditdate">
          To
          <input type="date" value={to} onChange={(e) => filter(setTo)(e.target.value)} />
        </label>
      </div>

      {error && (
        <div className="alert cmmsg" role="alert">
          {error}
        </div>
      )}

      {!data && !error && <SkeletonTable rows={10} cols={6} label="Loading the audit log" />}

      {data && data.items.length === 0 && <p className="cmempty">No event matches these filters.</p>}

      {data && data.items.length > 0 && (
        <div className="cmtable-wrap">
          <table className="cmtable audittable">
            <thead>
              <tr>
                <th>Time</th>
                <th>Kind</th>
                <th>User</th>
                <th>Request</th>
                <th>Status</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <Fragment key={e.id}>
                  <tr
                    className={open === e.id ? 'auditrow is-open' : 'auditrow'}
                    onClick={() => setOpen(open === e.id ? null : e.id)}
                    aria-expanded={open === e.id}
                  >
                    <td className="auditwhen">{when(e.at)}</td>
                    <td>
                      <span className={`audittag audittag--${e.level}`} title={LEVEL_LABELS[e.level]}>
                        {KIND_LABELS[e.kind] ?? e.kind}
                      </span>
                    </td>
                    <td>
                      {e.email ? (
                        <span className="audituser">
                          {e.email}
                          {e.role && <small>{e.role}</small>}
                        </span>
                      ) : (
                        <span className="auditmuted">signed out</span>
                      )}
                    </td>
                    <td className="auditpath">
                      {e.method && <b>{e.method}</b>} {e.path}
                    </td>
                    <td className="auditstatus">
                      {e.status ? e.status : ''}
                      {e.durationMs ? <small>{e.durationMs} ms</small> : null}
                    </td>
                    <td className="auditmsg">{e.message}</td>
                  </tr>
                  {open === e.id && (
                    <tr className="auditdetail">
                      <td colSpan={6}>
                        <Detail event={e} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > 0 && (
        <footer className="cmfoot">
          <span>
            Page {data.page} of {pages}
            {data.retentionDays > 0 && ` · events are kept ${data.retentionDays} days`}
          </span>
          <span className="cmspacer" />
          <span className="cmpager">
            <button type="button" className="cmbtn cmbtn--small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </button>
            <button
              type="button"
              className="cmbtn cmbtn--small"
              disabled={page >= pages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </span>
        </footer>
      )}
    </AdminShell>
  );
}

function Detail({ event }: { event: AuditEvent }) {
  const rows: [string, string | undefined][] = [
    ['Request id', event.requestId],
    ['User id', event.userId ? String(event.userId) : undefined],
    ['IP address', event.ip],
    ['Browser', event.userAgent],
  ];
  return (
    <div className="auditbox">
      <dl>
        {rows
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
      </dl>
      {event.detail && <pre className="auditpre">{event.detail}</pre>}
    </div>
  );
}
